import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  AlertTriangle, 
  Shield, 
  Phone, 
  Mail, 
  Clock, 
  FileText,
  Upload,
  Send,
  CheckCircle,
  X,
  Camera,
  Paperclip,
  User,
  Building,
  Calendar,
  MapPin,
  Wifi,
  Smartphone,
  Monitor,
  Server,
  Globe,
  Lock,
  Eye,
  AlertCircle,
  Info,
  ArrowRight,
  Download,
  Loader2
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const IncidentReporter = () => {
  const { user } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    incidentType: '',
    severity: '',
    title: '',
    description: '',
    dateTime: '',
    location: '',
    affectedSystems: [],
    reporterName: user?.user_metadata?.full_name || '',
    reporterEmail: user?.email || '',
    reporterPhone: '',
    department: '',
    agency: '',
    immediateActions: '',
    attachments: [],
    contactPreference: 'email'
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submittedTicket, setSubmittedTicket] = useState(null)
  const [uploadProgress, setUploadProgress] = useState({})

  const incidentTypes = [
    {
      id: 'malware',
      label: 'Malware/Virus',
      description: 'Suspicious software, viruses, or malicious programs',
      icon: Shield,
      color: 'text-red-600 bg-red-100'
    },
    {
      id: 'phishing',
      label: 'Phishing/Email Attack',
      description: 'Suspicious emails or phishing attempts',
      icon: Mail,
      color: 'text-orange-600 bg-orange-100'
    },
    {
      id: 'data-breach',
      label: 'Data Breach',
      description: 'Unauthorized access to sensitive data',
      icon: Lock,
      color: 'text-red-600 bg-red-100'
    },
    {
      id: 'unauthorized-access',
      label: 'Unauthorized Access',
      description: 'Suspicious login attempts or unauthorized system access',
      icon: Eye,
      color: 'text-yellow-600 bg-yellow-100'
    },
    {
      id: 'network-intrusion',
      label: 'Network Intrusion',
      description: 'Suspicious network activity or intrusion attempts',
      icon: Wifi,
      color: 'text-purple-600 bg-purple-100'
    },
    {
      id: 'system-compromise',
      label: 'System Compromise',
      description: 'Compromised servers, workstations, or devices',
      icon: Monitor,
      color: 'text-red-600 bg-red-100'
    },
    {
      id: 'social-engineering',
      label: 'Social Engineering',
      description: 'Suspicious phone calls or manipulation attempts',
      icon: Phone,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      id: 'other',
      label: 'Other Security Incident',
      description: 'Other cybersecurity-related incidents',
      icon: AlertTriangle,
      color: 'text-gray-600 bg-gray-100'
    }
  ]

  const severityLevels = [
    {
      id: 'critical',
      label: 'Critical',
      description: 'Immediate threat to operations or data',
      color: 'bg-red-500 text-white',
      priority: 'Immediate response required'
    },
    {
      id: 'high',
      label: 'High',
      description: 'Significant impact on systems or data',
      color: 'bg-orange-500 text-white',
      priority: 'Response within 2 hours'
    },
    {
      id: 'medium',
      label: 'Medium',
      description: 'Moderate impact, contained threat',
      color: 'bg-yellow-500 text-white',
      priority: 'Response within 24 hours'
    },
    {
      id: 'low',
      label: 'Low',
      description: 'Minor impact, suspicious activity',
      color: 'bg-green-500 text-white',
      priority: 'Response within 72 hours'
    }
  ]

  const affectedSystemOptions = [
    { id: 'email', label: 'Email System', icon: Mail },
    { id: 'network', label: 'Network Infrastructure', icon: Wifi },
    { id: 'servers', label: 'Servers', icon: Server },
    { id: 'workstations', label: 'Workstations/PCs', icon: Monitor },
    { id: 'mobile', label: 'Mobile Devices', icon: Smartphone },
    { id: 'website', label: 'Website/Web Applications', icon: Globe },
    { id: 'database', label: 'Databases', icon: FileText },
    { id: 'other', label: 'Other Systems', icon: AlertTriangle }
  ]

  const emergencyContacts = [
    {
      title: 'DICT Cybersecurity Hotline',
      phone: '(02) 8888-4242',
      email: 'cybersecurity@dict.gov.ph',
      hours: '24/7 Emergency Response'
    },
    {
      title: 'National Privacy Commission',
      phone: '(02) 8234-2228',
      email: 'info@privacy.gov.ph',
      hours: 'Mon-Fri 8:00 AM - 5:00 PM'
    },
    {
      title: 'PNP Anti-Cybercrime Group',
      phone: '(02) 8723-0401',
      email: 'info@pnp-acg.gov.ph',
      hours: '24/7 Emergency Response'
    }
  ]

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSystemToggle = (systemId) => {
    setFormData(prev => ({
      ...prev,
      affectedSystems: prev.affectedSystems.includes(systemId)
        ? prev.affectedSystems.filter(id => id !== systemId)
        : [...prev.affectedSystems, systemId]
    }))
  }

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files)
    const maxSize = 10 * 1024 * 1024 // 10MB
    
    // Validate file sizes
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`)
        return false
      }
      return true
    })
    
    if (validFiles.length === 0) return
    
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }))
  }

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }))
  }

  const uploadAttachments = async (incidentId) => {
    if (formData.attachments.length === 0) return []
    
    const uploadedPaths = []
    
    for (let i = 0; i < formData.attachments.length; i++) {
      const file = formData.attachments[i]
      const fileExt = file.name.split('.').pop()
      const fileName = `${incidentId}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }))
      
      try {
        const { data, error } = await supabase.storage
          .from('incident-attachments')
          .upload(`${user.id}/${fileName}`, file, {
            cacheControl: '3600',
            upsert: false
          })
        
        if (error) throw error
        
        uploadedPaths.push(data.path)
        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }))
      } catch (error) {
        console.error('Error uploading file:', error)
        alert(`Failed to upload ${file.name}. Please try again.`)
        throw error
      }
    }
    
    return uploadedPaths
  }
  
  const sendEmailNotification = async (incidentData) => {
    try {
      // Call Supabase Edge Function for email notification
      const { data, error } = await supabase.functions.invoke('send-incident-notification', {
        body: {
          incident: incidentData,
          recipientEmail: 'kelly.mdalumpines@gmail.com'
        }
      })
      
      if (error) {
        console.error('Email notification error:', error)
        // Don't throw error - incident should still be saved even if email fails
      }
    } catch (error) {
      console.error('Failed to send email notification:', error)
    }
  }
  
  const handleSubmit = async () => {
    setIsSubmitting(true)
    
    try {
      // Generate ticket number
      const ticketNumber = `IR-${Date.now().toString().slice(-8)}`
      
      // First, create the incident record
      const incidentData = {
        ticket_number: ticketNumber,
        incident_type: formData.incidentType,
        severity: formData.severity,
        title: formData.title,
        description: formData.description,
        incident_date_time: formData.dateTime,
        location: formData.location,
        affected_systems: formData.affectedSystems,
        immediate_actions: formData.immediateActions,
        reporter_name: formData.reporterName,
        reporter_email: formData.reporterEmail,
        reporter_phone: formData.reporterPhone,
        department: formData.department,
        agency: formData.agency,
        contact_preference: formData.contactPreference,
        reported_by: user.id,
        attachment_paths: []
      }
      
      const { data: incident, error: incidentError } = await supabase
        .from('incidents')
        .insert([incidentData])
        .select()
        .single()
      
      if (incidentError) throw incidentError
      
      // Upload attachments if any
      if (formData.attachments.length > 0) {
        const uploadedPaths = await uploadAttachments(incident.id)
        
        // Update incident with attachment paths
        const { error: updateError } = await supabase
          .from('incidents')
          .update({ attachment_paths: uploadedPaths })
          .eq('id', incident.id)
        
        if (updateError) throw updateError
        
        incident.attachment_paths = uploadedPaths
      }
      
      // Send email notification
      await sendEmailNotification(incident)
      
      setSubmittedTicket(incident)
      setIsSubmitted(true)
      
    } catch (error) {
      console.error('Error submitting incident:', error)
      alert('Failed to submit incident report. Please try again.')
    } finally {
      setIsSubmitting(false)
      setUploadProgress({})
    }
  }

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const getStepTitle = (step) => {
    switch (step) {
      case 1: return 'Incident Type & Severity'
      case 2: return 'Incident Details'
      case 3: return 'Reporter Information'
      case 4: return 'Review & Submit'
      default: return 'Report Incident'
    }
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card text-center py-12"
          >
            <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-success-600" />
            </div>
            <h1 className="text-3xl font-bold text-gov-800 mb-4">
              Incident Report Submitted Successfully
            </h1>
            <p className="text-lg text-gov-600 mb-6">
              Your incident report has been received and assigned ticket #{submittedTicket?.ticket_number}.
              Our cybersecurity team will review and respond according to the severity level.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2 mb-2">
                <Info className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-blue-800">Email Notification Sent</h4>
              </div>
              <p className="text-sm text-blue-700">
                A copy of your incident report has been sent to kelly.mdalumpines@gmail.com and your email address.
              </p>
            </div>
            <div className="bg-cyber-50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-cyber-800 mb-4">What happens next?</h3>
              <div className="space-y-3 text-left">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-cyber-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">1</span>
                  </div>
                  <span className="text-cyber-700">Initial assessment and triage (within 1 hour)</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-cyber-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">2</span>
                  </div>
                  <span className="text-cyber-700">Assignment to appropriate response team</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-cyber-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">3</span>
                  </div>
                  <span className="text-cyber-700">Investigation and containment measures</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-cyber-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">4</span>
                  </div>
                  <span className="text-cyber-700">Regular updates and final resolution report</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setIsSubmitted(false)
                  setCurrentStep(1)
                  setFormData({
                    incidentType: '',
                    severity: '',
                    title: '',
                    description: '',
                    dateTime: '',
                    location: '',
                    affectedSystems: [],
                    reporterName: '',
                    reporterEmail: '',
                    reporterPhone: '',
                    department: '',
                    agency: '',
                    immediateActions: '',
                    attachments: [],
                    contactPreference: 'email'
                  })
                }}
                className="btn-secondary"
              >
                Report Another Incident
              </button>
              <button className="btn-primary">
                Track Report Status
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-display font-bold text-gov-800 mb-4">
            Incident Reporter
          </h1>
          <p className="text-xl text-gov-600 max-w-3xl mx-auto">
            Report cybersecurity incidents quickly and securely. Our team will respond according to the severity level.
          </p>
        </motion.div>

        {/* Emergency Contacts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-8 bg-red-50 border-red-200"
        >
          <div className="flex items-center space-x-2 mb-4">
            <Phone className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold text-red-800">Emergency Contacts</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {emergencyContacts.map((contact, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border border-red-200">
                <h4 className="font-medium text-red-800 mb-2">{contact.title}</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center space-x-2">
                    <Phone className="w-3 h-3 text-red-600" />
                    <span className="text-red-700">{contact.phone}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-3 h-3 text-red-600" />
                    <span className="text-red-700">{contact.email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-3 h-3 text-red-600" />
                    <span className="text-red-700">{contact.hours}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Progress Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step <= currentStep ? 'bg-cyber-600 text-white' : 'bg-gov-200 text-gov-600'
                }`}>
                  {step < currentStep ? <CheckCircle size={16} /> : step}
                </div>
                {step < 4 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step < currentStep ? 'bg-cyber-600' : 'bg-gov-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <h2 className="text-xl font-semibold text-gov-800 text-center">
            Step {currentStep}: {getStepTitle(currentStep)}
          </h2>
        </motion.div>

        {/* Form Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="card"
        >
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gov-800 mb-4">Select Incident Type</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {incidentTypes.map((type) => {
                    const TypeIcon = type.icon
                    return (
                      <div
                        key={type.id}
                        onClick={() => handleInputChange('incidentType', type.id)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                          formData.incidentType === type.id
                            ? 'border-cyber-500 bg-cyber-50'
                            : 'border-gov-200 hover:border-cyber-300'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type.color}`}>
                            <TypeIcon size={20} />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gov-800">{type.label}</h4>
                            <p className="text-sm text-gov-600 mt-1">{type.description}</p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gov-800 mb-4">Select Severity Level</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {severityLevels.map((level) => (
                    <div
                      key={level.id}
                      onClick={() => handleInputChange('severity', level.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                        formData.severity === level.id
                          ? 'border-cyber-500 bg-cyber-50'
                          : 'border-gov-200 hover:border-cyber-300'
                      }`}
                    >
                      <div className={`w-full py-2 px-3 rounded-lg text-center font-medium mb-3 ${level.color}`}>
                        {level.label}
                      </div>
                      <p className="text-sm text-gov-600 mb-2">{level.description}</p>
                      <p className="text-xs text-gov-500">{level.priority}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gov-700 mb-2">
                  Incident Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Brief description of the incident"
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gov-700 mb-2">
                  Detailed Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Provide detailed information about what happened, when it was discovered, and any symptoms observed..."
                  rows={6}
                  className="input-field"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-2">
                    Date & Time of Incident *
                  </label>
                  {/* Check if browser is Firefox */}
                  {navigator.userAgent.toLowerCase().indexOf('firefox') > -1 ? (
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={formData.dateTime ? formData.dateTime.split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value;
                          const time = formData.dateTime ? formData.dateTime.split('T')[1] || '00:00' : '00:00';
                          handleInputChange('dateTime', `${date}T${time}`);
                        }}
                        className="input-field"
                        required
                      />
                      <input
                        type="time"
                        value={formData.dateTime ? formData.dateTime.split('T')[1] || '' : ''}
                        onChange={(e) => {
                          const time = e.target.value;
                          const date = formData.dateTime ? formData.dateTime.split('T')[0] : new Date().toISOString().split('T')[0];
                          handleInputChange('dateTime', `${date}T${time}`);
                        }}
                        className="input-field"
                        required
                      />
                    </div>
                  ) : (
                    <input
                      type="datetime-local"
                      value={formData.dateTime}
                      onChange={(e) => handleInputChange('dateTime', e.target.value)}
                      className="input-field"
                      required
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="Building, floor, room number"
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gov-700 mb-2">
                  Affected Systems (Select all that apply)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {affectedSystemOptions.map((system) => {
                    const SystemIcon = system.icon
                    return (
                      <div
                        key={system.id}
                        onClick={() => handleSystemToggle(system.id)}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                          formData.affectedSystems.includes(system.id)
                            ? 'border-cyber-500 bg-cyber-50'
                            : 'border-gov-200 hover:border-cyber-300'
                        }`}
                      >
                        <div className="text-center">
                          <SystemIcon className="w-6 h-6 mx-auto mb-2 text-gov-600" />
                          <span className="text-xs text-gov-700">{system.label}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gov-700 mb-2">
                  Immediate Actions Taken
                </label>
                <textarea
                  value={formData.immediateActions}
                  onChange={(e) => handleInputChange('immediateActions', e.target.value)}
                  placeholder="Describe any immediate actions taken to contain or mitigate the incident..."
                  rows={4}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gov-700 mb-2">
                  Attachments (Screenshots, logs, evidence)
                </label>
                <div className="border-2 border-dashed border-gov-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    accept=".jpg,.jpeg,.png,.pdf,.txt,.log,.zip"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gov-400 mx-auto mb-2" />
                    <p className="text-gov-600">Click to upload files or drag and drop</p>
                    <p className="text-sm text-gov-500 mt-1">PNG, JPG, PDF, TXT, LOG, ZIP up to 10MB each</p>
                  </label>
                </div>
                {formData.attachments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {formData.attachments.map((file, index) => {
                      const progress = uploadProgress[file.name] || 0
                      const isUploading = isSubmitting && progress < 100
                      
                      return (
                        <div key={index} className="bg-gov-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Paperclip className="w-4 h-4 text-gov-600" />
                              <span className="text-sm text-gov-700">{file.name}</span>
                              <span className="text-xs text-gov-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                            {!isSubmitting && (
                              <button
                                onClick={() => removeAttachment(index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                          {isUploading && (
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-cyber-600 h-2 rounded-full transition-all duration-300" 
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.reporterName}
                    onChange={(e) => handleInputChange('reporterName', e.target.value)}
                    placeholder="Your full name"
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.reporterEmail}
                    onChange={(e) => handleInputChange('reporterEmail', e.target.value)}
                    placeholder="your.email@agency.gov.ph"
                    className="input-field"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.reporterPhone}
                    onChange={(e) => handleInputChange('reporterPhone', e.target.value)}
                    placeholder="(02) 1234-5678"
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-2">
                    Department/Division *
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    placeholder="IT Department, HR, Finance, etc."
                    className="input-field"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gov-700 mb-2">
                  Agency/Organization *
                </label>
                <input
                  type="text"
                  value={formData.agency}
                  onChange={(e) => handleInputChange('agency', e.target.value)}
                  placeholder="Department of Information and Communications Technology"
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gov-700 mb-2">
                  Preferred Contact Method
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="email"
                      checked={formData.contactPreference === 'email'}
                      onChange={(e) => handleInputChange('contactPreference', e.target.value)}
                      className="mr-2"
                    />
                    <Mail className="w-4 h-4 mr-1" />
                    Email
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="phone"
                      checked={formData.contactPreference === 'phone'}
                      onChange={(e) => handleInputChange('contactPreference', e.target.value)}
                      className="mr-2"
                    />
                    <Phone className="w-4 h-4 mr-1" />
                    Phone
                  </label>
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gov-800 mb-4">Review Your Report</h3>
              
              <div className="bg-gov-50 rounded-lg p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gov-600">Incident Type:</span>
                    <p className="text-gov-800">
                      {incidentTypes.find(t => t.id === formData.incidentType)?.label || 'Not selected'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gov-600">Severity:</span>
                    <p className="text-gov-800">
                      {severityLevels.find(s => s.id === formData.severity)?.label || 'Not selected'}
                    </p>
                  </div>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gov-600">Title:</span>
                  <p className="text-gov-800">{formData.title || 'Not provided'}</p>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gov-600">Description:</span>
                  <p className="text-gov-800">{formData.description || 'Not provided'}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gov-600">Date & Time:</span>
                    <p className="text-gov-800">
                      {formData.dateTime ? new Date(formData.dateTime).toLocaleString() : 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gov-600">Location:</span>
                    <p className="text-gov-800">{formData.location || 'Not provided'}</p>
                  </div>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gov-600">Affected Systems:</span>
                  <p className="text-gov-800">
                    {formData.affectedSystems.length > 0
                      ? formData.affectedSystems.map(id => 
                          affectedSystemOptions.find(s => s.id === id)?.label
                        ).join(', ')
                      : 'None selected'
                    }
                  </p>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gov-600">Reporter:</span>
                  <p className="text-gov-800">
                    {formData.reporterName} ({formData.reporterEmail})
                  </p>
                  <p className="text-gov-800">
                    {formData.department}, {formData.agency}
                  </p>
                </div>
                
                {formData.attachments.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-gov-600">Attachments:</span>
                    <p className="text-gov-800">
                      {formData.attachments.length} file(s) attached
                    </p>
                  </div>
                )}
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Important Notice</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      By submitting this report, you confirm that the information provided is accurate to the best of your knowledge. 
                      False reporting of cybersecurity incidents may result in disciplinary action.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t border-gov-200">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`btn-secondary flex items-center space-x-2 ${
                currentStep === 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <span>Previous</span>
            </button>
            
            {currentStep < 4 ? (
              <button
                onClick={nextStep}
                disabled={(
                  currentStep === 1 && (!formData.incidentType || !formData.severity)
                ) || (
                  currentStep === 2 && (!formData.title || !formData.description || !formData.dateTime)
                ) || (
                  currentStep === 3 && (!formData.reporterName || !formData.reporterEmail || !formData.department || !formData.agency)
                )}
                className="btn-primary flex items-center space-x-2"
              >
                <span>Next</span>
                <ArrowRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-primary flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    <span>Submit Report</span>
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default IncidentReporter